import React, { useState } from 'react';
import './Tutorial.css';

function Tutorial({ onClose, isHost = false }) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const hostSlides = [
    {
      icon: '🎮',
      title: 'Welcome to Buzzer Bash!',
      content: 'You\'re the host! Your screen will display questions and scores for everyone to see.',
      tip: 'Cast or mirror this screen to a TV for the best experience!'
    },
    {
      icon: '📱',
      title: 'Get Players to Join',
      content: 'Share the room code with your friends. They\'ll scan the QR code or enter the code on their phones.',
      tip: 'Up to 10 players can join each game'
    },
    {
      icon: '🎯',
      title: 'Game Modes',
      content: 'Each round has a different mode:\n• Quick Fire - Speed matters!\n• Double Trouble - 2x points\n• Point Steal - Wrong answers help others\n• Bomb Pass - Don\'t hold the bomb!\n• Final Countdown - Survive to win!',
      tip: 'Modes are randomly selected each round'
    },
    {
      icon: '🏆',
      title: 'Scoring',
      content: 'Players earn points for correct answers. Faster answers = more points!\nBuild streaks for bonus multipliers.',
      tip: 'Watch the scoreboard to track the competition'
    },
    {
      icon: '⚙️',
      title: 'Settings',
      content: 'Click the settings gear to:\n• Select which trivia topics to include\n• Choose how many rounds to play',
      tip: 'Pause the game anytime if needed'
    },
    {
      icon: '🔊',
      title: 'Audio',
      content: 'Enable audio for voice announcements! Questions are read aloud for accessibility.',
      tip: 'Click "Enable Audio" when the game starts'
    }
  ];

  const playerSlides = [
    {
      icon: '👋',
      title: 'Welcome, Player!',
      content: 'You\'ll answer trivia questions on your phone. Be fast and be right!',
      tip: 'Keep your phone charged and stay connected'
    },
    {
      icon: '🎯',
      title: 'How to Play',
      content: '1. Wait for the question to appear\n2. Tap the answer you think is correct\n3. First correct answer wins the most points!',
      tip: 'Speed matters, but accuracy matters more'
    },
    {
      icon: '🔥',
      title: 'Streaks',
      content: 'Get multiple questions right in a row to build a streak. Streaks give bonus points!',
      tip: 'A 5-streak can double your points'
    },
    {
      icon: '🎲',
      title: 'Game Modes',
      content: 'Each round has a special twist:\n• Quick Fire - Speed bonus\n• Double Trouble - 2x points\n• Point Steal - Steal points!\n• Bomb Pass - Pass the bomb\n• Final Countdown - Last one standing',
      tip: 'Pay attention to the round instructions'
    },
    {
      icon: '📊',
      title: 'Scoreboard',
      content: 'Your score and rank are shown after each question. Climb to the top!',
      tip: 'Watch the host screen for full standings'
    },
    {
      icon: '🔄',
      title: 'Stay Connected',
      content: 'If you get disconnected, don\'t worry! Just re-enter your name and room code to rejoin.',
      tip: 'Your progress is saved for quick reconnection'
    }
  ];

  const slides = isHost ? hostSlides : playerSlides;

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      onClose();
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <div className="tutorial-overlay" onClick={onClose}>
      <div className="tutorial-modal" onClick={(e) => e.stopPropagation()}>
        <button className="tutorial-close" onClick={onClose}>×</button>
        
        <div className="tutorial-content">
          <div className="tutorial-slide">
            <div className="slide-icon">{slides[currentSlide].icon}</div>
            <h2 className="slide-title">{slides[currentSlide].title}</h2>
            <div className="slide-content">
              {slides[currentSlide].content.split('\n').map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
            {slides[currentSlide].tip && (
              <div className="slide-tip">
                <span className="tip-icon">💡</span>
                <span className="tip-text">{slides[currentSlide].tip}</span>
              </div>
            )}
          </div>
        </div>

        {/* Progress dots */}
        <div className="tutorial-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`tutorial-dot ${currentSlide === index ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="tutorial-nav">
          <button 
            className="btn-tutorial-prev"
            onClick={prevSlide}
            disabled={currentSlide === 0}
          >
            ← Back
          </button>
          <span className="slide-counter">{currentSlide + 1} / {slides.length}</span>
          <button 
            className="btn-tutorial-next"
            onClick={nextSlide}
          >
            {currentSlide === slides.length - 1 ? 'Got it!' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Tutorial;

